import { Card } from "@/components/ui/card"
import { Users, CheckCircle, Clock, TrendingUp } from "lucide-react"

interface ClassPerformanceTableProps {
  classes: Array<{
    id: number
    className: string
    teacher: string
    totalTasks: string
    progress: number
    status: string
    statusColor: string
  }>
}

export function ClassPerformanceTable({ classes }: ClassPerformanceTableProps) {
  // Calculate overall completion rate
  const overallCompletion = classes.reduce((sum, classItem) => {
    const completed = parseInt(classItem.totalTasks.split('/')[0])
    const total = parseInt(classItem.totalTasks.split('/')[1])
    return sum + (completed / total)
  }, 0) / classes.length * 100

  return (
    <Card className="p-4 border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-50 rounded-md">
            <TrendingUp className="w-3.5 h-3.5 text-blue-600" />
          </div>
          <h3 className="text-sm font-semibold text-gray-900">Class Performance</h3>
        </div>
       
      </div>

      {/* Minimal Table */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[400px]">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Class</th>
              <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Teacher</th>
              <th className="text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase">Progress</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {classes.slice(0, 5).map((classItem) => {
              const [completed, total] = classItem.totalTasks.split('/').map(Number)
              const completionRate = total > 0 ? (completed / total) * 100 : 0
              
              return (
                <tr 
                  key={classItem.id} 
                  className="hover:bg-gray-50/50 transition-colors duration-150 group"
                >
                  <td className="py-2.5 px-2">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Users className="w-3 h-3 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{classItem.className}</p>
                        <p className="text-xs text-gray-400">ID: {classItem.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-2">
                    <p className="text-sm text-gray-700">{classItem.teacher}</p>
                  </td>
                  <td className="py-2.5 px-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-500"
                            style={{ 
                              width: `${completionRate}%`,
                              backgroundColor: classItem.statusColor || (completionRate >= 80 ? '#10b981' : completionRate >= 50 ? '#f59e0b' : '#ef4444')
                            }}
                          />
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-gray-500">{completed}/{total}</span>
                          <span className="text-xs font-medium text-gray-700">{completionRate.toFixed(0)}%</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2.5 px-2">
                    {/* <div className="flex items-center gap-1.5">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                        completionRate >= 80 
                          ? 'bg-green-100 text-green-800' 
                          : completionRate >= 50 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {completionRate >= 80 ? 'High' : completionRate >= 50 ? 'Medium' : 'Low'}
                      </span>
                      {classItem.status === 'active' ? (
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                      ) : (
                        <Clock className="w-3 h-3 text-gray-400" />
                      )}
                    </div> */}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Footer Summary */}
      {classes.length > 5 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Showing 5 of {classes.length} classes
            </p>
    
          </div>
        </div>
      )}
    </Card>
  )
}